import React from 'react';

import {NOTICE_TYPES} from 'src/features/lohp/hooks/useFetchHeroBanners';

import {ConsolidatedHeroBanner, ConsolidatedHeroBannerSkeleton} from '../consolidated-hero-banner';
import {MarketingHeroBannerProps} from '../marketing-hero-banner.react-component';

/**
 * Routes between the consolidated (variant) and marketing (control) hero banner
 * based on the notice type returned by the backend:
 *   - NOTICE_TYPES.LOHP_HERO_BANNER ('lohp_hero_banner') -> ConsolidatedHeroBanner
 *   - anything else (e.g. 'banner_carousel_slide', FALLBACK_BANNER) -> WrappedComponent
 *
 * The variant is no longer driven by the LOHP_HERO_BANNER_CONSOLIDATED feature
 * flag on the client; useFetchHeroBanners fires the variant and control
 * requests in parallel and sets noticeType to LOHP_HERO_BANNER when the variant
 * request succeeds, otherwise to BANNER_CAROUSEL_SLIDE when the control
 * request succeeds.
 */
export const WithConsolidatedHeroBanner = (
    WrappedComponent: React.ComponentType<MarketingHeroBannerProps>,
) => {
    const WithConsolidatedWrapper = (defaultProps: MarketingHeroBannerProps) => {
        // We don't know which variant will win until the response lands, so use
        // the neutral skeleton during the fetch.
        if (defaultProps.isLoading) {
            return <ConsolidatedHeroBannerSkeleton className={defaultProps.className} />;
        }

        if (!defaultProps.heroBanners?.length) {
            return <WrappedComponent {...defaultProps} />;
        }

        if (defaultProps.noticeType !== NOTICE_TYPES.LOHP_HERO_BANNER) {
            return <WrappedComponent {...defaultProps} />;
        }

        return <ConsolidatedHeroBanner {...defaultProps} />;
    };

    return WithConsolidatedWrapper;
};
